
import { FileText, MessageSquare } from 'lucide-react';
import { useState } from 'react';
import PageLayout from '@/components/layout/PageLayout';
import DocCard from '@/components/help/DocCard';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';

const Help = () => {
  const [isFaqOpen, setIsFaqOpen] = useState(false);

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@recallio.ai?subject=Need help with Recallio';
  };

  const faqData = [
    {
      section: "🔒 Memory Infrastructure & Security",
      questions: [
        {
          question: "1. What is scoped memory and how does Recallio enforce it?",
          answer: "Scoped memory means each memory entry is tied to a specific user_id, project_id, or team_id, ensuring strict data isolation. Recallio enforces this via metadata tagging and access control at the API key level."
        },
        {
          question: "2. How does TTL (Time-to-Live) work and what happens when memory expires?",
          answer: "Each memory entry can include a TTL value. Once expired, a scheduled job deletes the entry and logs the deletion event for auditability."
        },
        {
          question: "3. Can I audit all memory actions (write, recall, delete)?",
          answer: "Yes, every action is logged with metadata (timestamp, scope, success) and available via the /logs API."
        },
        {
          question: "4. How do I export all memory for a user or project?",
          answer: "Use the /memory/export API with filters for user/project scope. Supports JSON or CSV output."
        }
      ]
    },
    {
      section: "👮 Compliance & Privacy",
      questions: [
        {
          question: "5. Is Recallio GDPR and HIPAA compliant out of the box?",
          answer: "Yes, Recallio includes TTL expiration, Right to Be Forgotten, audit logs, consent flags, and scoped isolation, enabling compliance alignment."
        },
        {
          question: "6. How does the Right to Be Forgotten API work?",
          answer: "Call /memory/delete with a user_id or project_id to erase all related memory. Deletion events are logged for compliance."
        },
        {
          question: "7. Can users revoke consent for memory storage?",
          answer: "Yes, memory writes require a consent_flag. Entries without explicit consent can be rejected or soft-deleted based on admin policy."
        }
      ]
    },
    {
      section: "⚙️ API & Developer Tools",
      questions: [
        {
          question: "8. Where can I find the API documentation and SDKs?",
          answer: null,
          hasButton: true,
          buttonText: "View API Documentation",
          buttonLink: "/api-docs"
        },
        {
          question: "9. How do I test recall performance?",
          answer: "Use the /latency-test endpoint to benchmark average response time on a fixed dataset—ideal for SLAs or enterprise evaluation."
        },
        {
          question: "10. How do I debug unexpected memory recall behavior?",
          answer: "Use the /memory/view API to inspect raw memory chunks, metadata, and optionally the embedding vector used for recall."
        }
      ]
    },
    {
      section: "🛠 Admin Dashboard & Controls",
      questions: [
        {
          question: "11. How can I monitor API usage and memory growth?",
          answer: "The admin dashboard shows per-project usage stats (writes, recalls), memory footprint over time, and TTL statuses."
        },
        {
          question: "12. How are API keys managed and scoped?",
          answer: "Each API key is tied to a project or tenant. Keys enforce scope restrictions and rate limits automatically via Supabase/Auth layer."
        },
        {
          question: "13. Can I control access by role (admin vs viewer)?",
          answer: "Yes, the admin UI supports role-based access—admins can write/delete/export; viewers can only see memory stats or TTL states."
        }
      ]
    }
  ];

  return (
    <PageLayout 
      title="Help & Resources" 
      description="Documentation and support resources"
    >
      {/* Need Help Section - No Title */}
      <div className="mb-8 w-full">
        <p className="text-muted-foreground mb-4">
          Our support team is available Monday through Friday, 9am-5pm ET.
          We typically respond to all inquiries within 24 hours.
        </p>
        
        <Button onClick={handleContactSupport} className="btn-primary flex items-center gap-2">
          <MessageSquare size={16} />
          Contact Support
        </Button>
      </div>

      {/* Documentation Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
        <DocCard
          title="API Reference"
          description="Comprehensive API documentation with examples, parameters, and response formats."
          icon={<FileText size={18} className="text-recallio-yellow" />}
          href="/api-docs"
          isInternalLink={true}
        />
        <DocCard
          title="Frequently Asked Questions"
          description="Find answers to common questions about usage, billing, and technical topics."
          icon={<MessageSquare size={18} className="text-recallio-yellow" />}
          href="#"
          onClick={() => setIsFaqOpen(true)}
        />
      </div>


      {/* FAQ Modal */}
      <Dialog open={isFaqOpen} onOpenChange={setIsFaqOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Frequently Asked Questions</DialogTitle>
            <DialogDescription>
              Find answers to common questions about Recallio
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <Accordion type="single" collapsible defaultValue="item-0-0" className="w-full">
              {faqData.map((section, sectionIndex) => (
                <div key={sectionIndex} className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-recallio-yellow">{section.section}</h3>
                  {section.questions.map((faq, questionIndex) => (
                    <AccordionItem key={`${sectionIndex}-${questionIndex}`} value={`item-${sectionIndex}-${questionIndex}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        {faq.hasButton ? (
                          <Button asChild className="btn-primary">
                            <Link to={faq.buttonLink}>
                              {faq.buttonText}
                            </Link>
                          </Button>
                        ) : (
                          faq.answer
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </div>
              ))}
            </Accordion>
          </div>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default Help;
